"use client";

import React, { useMemo, useState } from "react";
import Image from "next/image";
import { LinkTrackers } from "~/components/reusables/link-trackers";
import {
  getRecentMediaIconSrc,
  type RecentMediaItem,
} from "~/lib/blog-author-recent-media";

const INITIAL_VISIBLE_COUNT = 3;
const LOAD_MORE_COUNT = 3;

type RecentMediaProps = {
  media?: RecentMediaItem[];
};

export const RecentMedia = ({ media = [] }: RecentMediaProps) => {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

  const visibleMedia = useMemo(
    () => media.slice(0, visibleCount),
    [media, visibleCount],
  );

  if (media.length === 0) {
    return null;
  }

  const hasMore = visibleCount < media.length;

  return (
    <div className="container relative mt-[60px] mb-16">
      <div className="mb-[50px] flex items-center md:gap-8">
        <h2 className="font-h2 inline-block bg-white text-black">
          Recent Media
        </h2>
      </div>

      <div className="flex flex-col gap-6">
        {visibleMedia.map((item) => (
          <div
            key={item.id}
            className="flex flex-col justify-between gap-6 border-b border-gray-200 pb-6 md:flex-row md:items-center md:gap-8 lg:gap-14"
          >
            <div className="flex flex-1 items-center gap-6 md:gap-10">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center">
                <Image
                  unoptimized
                  src={getRecentMediaIconSrc(item.iconType)}
                  alt=""
                  aria-hidden
                  width={48}
                  height={48}
                  className="h-10 w-10 object-contain md:h-12 md:w-12"
                />
              </div>
              <p className="max-w-[800px] text-lg font-medium leading-tight text-gray-800 md:text-xl">
                {item.text}
              </p>
            </div>

            <div className="w-full flex-shrink-0 md:w-[160px]">
              <LinkTrackers
                href={item.href}
                target={item.href.startsWith("/") ? "_self" : "_blank"}
                segmentMsg="Resource clicked"
                segmentOpt={item.segmentOpt}
                aria-label={item.buttonText}
                className="group relative block h-[72px] w-full overflow-hidden rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black md:w-[160px]"
              >
                <div
                  aria-hidden
                  className="absolute left-0 top-0 h-full w-full rounded-lg blur group-hover:bg-gradient-to-r group-hover:from-[#ebe412b3] group-hover:to-[#68ce8db3] group-active:from-[#ABD84F] group-active:to-[#09C0E9]"
                />
                <span className="relative flex h-[72px] w-full items-center justify-center rounded-lg border-none bg-gradient-to-r from-[#ABD84F] to-[#09C0E9] px-5 text-black hover:from-[#EBE212] hover:to-[#68CE8E] active:from-[#ABD84F] active:to-[#09C0E9] md:w-[160px]">
                  <span className="text-[22px] font-normal">{item.buttonText}</span>
                </span>
              </LinkTrackers>
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="mt-[50px]">
          <button
            type="button"
            onClick={() =>
              setVisibleCount((count) =>
                Math.min(count + LOAD_MORE_COUNT, media.length),
              )
            }
            className="h-[50px] w-[180px] rounded-lg border border-gray-400 bg-white px-6 py-2 text-black transition-colors hover:bg-gray-100"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};
